import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { ClockCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import { errMsg } from '../../lib/errors';
import { ListenerName } from '../../lib/listenerName';
import { useUrlState, intParam, dayParam } from '../../lib/urlState';
import {
  AVAILABILITY_BASIS_OPTIONS,
  declineRuleLabel,
  EMPTY_PERIOD_FORM,
  formFromPeriod,
  MAX_REWARD_RUPEES,
  NAME_MAX,
  paiseToRupees,
  periodBody,
  periodError,
  periodWindowLabel,
  timezoneOptions,
  type AvailabilityBasis,
  type BonusRewardRow,
  type PeriodForm,
  type TimeBonusConfig,
  type TimeBonusPeriod,
} from './listenerBonus';

export const TIME_BONUS_KEY = ['admin', 'time-bonus', 'config'];
const REWARDS_KEY = ['admin', 'time-bonus', 'rewards'];

/**
 * Listener Online Availability Bonus — admin-configured daily periods (Night
 * Bonus, Early Morning Bonus, …) that automatically pay a listener's earnings
 * wallet for every completed target of QUALIFYING ONLINE AVAILABILITY inside
 * the period: online-session time minus time occupied on a connected call and
 * minus decline-penalty pauses. Rewards are credited server-side, one ledger
 * transaction per completed target.
 */
export function ListenerBonusPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('time_bonus.edit');

  const { data, isLoading, error } = useQuery({
    queryKey: TIME_BONUS_KEY,
    queryFn: async () => (await api.get<TimeBonusConfig>('/admin/time-bonus')).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: TIME_BONUS_KEY });

  // ── Global settings (master switch + timezone) ───────────────────────────
  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [basis, setBasis] = useState<AvailabilityBasis>('callable');
  useEffect(() => {
    // Filling the form when the data arrives — the accepted non-fetch useEffect.
    if (data) {
      setEnabled(data.enabled);
      setTimezone(data.timezone);
      setBasis(data.availabilityBasis ?? 'callable');
    }
  }, [data]);

  const saveSettings = useMutation({
    mutationFn: () =>
      api.put('/admin/time-bonus/settings', { enabled, timezone, availabilityBasis: basis }),
    onSuccess: () => {
      message.success('Bonus settings saved');
      void refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Could not save the settings')),
  });

  // ── Periods CRUD ─────────────────────────────────────────────────────────
  /** null = closed, 'new' = creating, otherwise the period being edited. */
  const [editing, setEditing] = useState<TimeBonusPeriod | 'new' | null>(null);
  const [form, setForm] = useState<PeriodForm>(EMPTY_PERIOD_FORM);
  const periods = useMemo(() => data?.periods ?? [], [data]);

  const openCreate = () => {
    setForm(EMPTY_PERIOD_FORM);
    setEditing('new');
  };
  const openEdit = (p: TimeBonusPeriod) => {
    setForm(formFromPeriod(p));
    setEditing(p);
  };
  const othersFor = (editingNow: TimeBonusPeriod | 'new') =>
    periods.filter((p) => editingNow === 'new' || p.id !== editingNow.id);

  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? api.post('/admin/time-bonus/periods', periodBody(form))
        : api.patch(`/admin/time-bonus/periods/${(editing as TimeBonusPeriod).id}`, periodBody(form)),
    onSuccess: () => {
      message.success(editing === 'new' ? 'Bonus period created' : 'Bonus period updated');
      setEditing(null);
      void refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Could not save the period')),
  });

  const toggle = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      api.patch(`/admin/time-bonus/periods/${id}`, { enabled: on }),
    onSuccess: () => void refresh(),
    onError: (e) => message.error(errMsg(e, 'Could not change the status')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/time-bonus/periods/${id}`),
    onSuccess: () => {
      message.success('Bonus period removed — already-paid rewards are kept');
      void refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Could not remove the period')),
  });

  const columns: TableColumnsType<TimeBonusPeriod> = [
    {
      title: 'Period',
      render: (_: unknown, p) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{p.name}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {periodWindowLabel(p)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Reward',
      width: 300,
      render: (_: unknown, p) => (
        <Space direction="vertical" size={0}>
          <span>
            {paiseToRupees(p.rewardPaise)} / {p.targetMinutes} min online availability
          </span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {declineRuleLabel(p.penalty)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      width: 150,
      render: (_: unknown, p) => (
        <Space>
          <Tag color={p.enabled ? 'green' : 'default'}>{p.enabled ? 'Active' : 'Off'}</Tag>
          <Switch
            size="small"
            aria-label={`${p.enabled ? 'Disable' : 'Enable'} ${p.name}`}
            checked={p.enabled}
            loading={toggle.isPending && toggle.variables?.id === p.id}
            disabled={toggle.isPending || !canEdit}
            onChange={(v) => toggle.mutate({ id: p.id, on: v })}
          />
        </Space>
      ),
    },
    {
      title: '',
      width: 110,
      render: (_: unknown, p) => (
        <Space>
          <Can permission="time_bonus.edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(p)} aria-label={`Edit ${p.name}`} />
            <Popconfirm
              title="Remove this bonus period?"
              description="Already-paid rewards keep their records; nothing new accrues."
              okText="Remove"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove.mutate(p.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} aria-label={`Remove ${p.name}`} />
            </Popconfirm>
          </Can>
        </Space>
      ),
    },
  ];

  const formProblem = editing ? periodError(form, othersFor(editing)) : null;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Listener online availability bonus</span>
          </Space>
        }
        extra={
          <Can permission="time_bonus.edit">
            <Button
              type="primary"
              loading={saveSettings.isPending}
              onClick={() => saveSettings.mutate()}
            >
              Save settings
            </Button>
          </Can>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Only qualifying ONLINE AVAILABILITY counts — time genuinely available to receive calls."
            description="Time on a connected call is occupied and does not accrue, and too many declined or unanswered rings pause accrual (the period's decline rule). A session crossing a period boundary is split exactly at the boundary, and leftover minutes do not carry into another period or day. Rewards are credited automatically to the listener's earnings wallet — one transaction per completed target."
          />
          <Row gutter={16} align="middle">
            <Col>
              <Space>
                <Switch checked={enabled} onChange={setEnabled} disabled={!canEdit} />
                <span style={{ fontWeight: 600 }}>{enabled ? 'Bonus system ON' : 'Bonus system OFF'}</span>
              </Space>
            </Col>
            <Col flex="auto" />
            <Col>
              <Space>
                <span>Business timezone</span>
                <Select
                  showSearch
                  style={{ width: 240 }}
                  value={timezone}
                  onChange={setTimezone}
                  disabled={!canEdit}
                  options={timezoneOptions(timezone).map((z) => ({ value: z, label: z }))}
                />
              </Space>
            </Col>
          </Row>
          <Row gutter={16} align="middle">
            <Col>
              <Space>
                <span>Availability basis</span>
                <Select<AvailabilityBasis>
                  style={{ width: 460 }}
                  value={basis}
                  onChange={setBasis}
                  disabled={!canEdit}
                  options={AVAILABILITY_BASIS_OPTIONS}
                />
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      <Card
        title="Bonus periods"
        extra={
          <Can permission="time_bonus.edit">
            <Button icon={<PlusOutlined />} onClick={openCreate}>
              Add period
            </Button>
          </Can>
        }
      >
        {error ? (
          <Alert type="error" showIcon message={errMsg(error, 'Could not load the bonus configuration')} />
        ) : (
          <Table<TimeBonusPeriod>
            rowKey="id"
            size="middle"
            loading={isLoading}
            columns={columns}
            dataSource={periods}
            pagination={false}
            locale={{ emptyText: 'No bonus periods yet' }}
          />
        )}
      </Card>

      <RewardsCard />

      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'New bonus period' : 'Edit bonus period'}
        okText="Save"
        okButtonProps={{ disabled: formProblem !== null, loading: save.isPending }}
        onOk={() => save.mutate()}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <div style={{ marginBottom: 4 }}>Name</div>
            <Input
              value={form.name}
              maxLength={NAME_MAX}
              placeholder="Night Bonus"
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>Start time</div>
              <Input
                type="time"
                aria-label="Start time"
                value={form.startTime}
                onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
              />
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>End time (exclusive)</div>
              <Input
                type="time"
                aria-label="End time"
                value={form.endTime}
                onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
              />
            </Col>
          </Row>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            An end time at or before the start makes the period cross midnight (e.g. 21:00 → 03:00).
          </Typography.Text>
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>Reward per completed target (₹)</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0.01}
                max={MAX_REWARD_RUPEES}
                step={1}
                value={form.rewardRupees}
                onChange={(v) => setForm((f) => ({ ...f, rewardRupees: v }))}
              />
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>Qualifying online minutes per reward</div>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={1440}
                step={5}
                value={form.targetMinutes}
                onChange={(v) => setForm((f) => ({ ...f, targetMinutes: v }))}
              />
            </Col>
          </Row>
          <Typography.Text strong>Decline rule</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Declining or not answering more rings than allowed pauses the bonus accrual. Time on a
            connected call never counts — only genuine availability does.
          </Typography.Text>
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>Allowed misses</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                value={form.maxMisses}
                onChange={(v) => setForm((f) => ({ ...f, maxMisses: v }))}
              />
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>Evaluation window (minutes)</div>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={1440}
                step={5}
                value={form.evalWindowMinutes}
                onChange={(v) => setForm((f) => ({ ...f, evalWindowMinutes: v }))}
              />
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>Recovery</div>
              <Select<PeriodForm['recovery']>
                style={{ width: '100%' }}
                value={form.recovery}
                onChange={(v) => setForm((f) => ({ ...f, recovery: v }))}
                options={[
                  { value: 'cooldown', label: 'After a cooldown' },
                  { value: 'window_end', label: 'Not before the window ends' },
                ]}
              />
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>Cooldown (minutes)</div>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={1440}
                step={5}
                disabled={form.recovery === 'window_end'}
                value={form.cooldownMinutes}
                onChange={(v) => setForm((f) => ({ ...f, cooldownMinutes: v }))}
              />
            </Col>
          </Row>
          <Space>
            <Switch
              size="small"
              checked={form.enabled}
              onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            />
            <span>Enabled</span>
          </Space>
          {formProblem && <Alert type="warning" showIcon message={formProblem} />}
        </Space>
      </Modal>
    </Space>
  );
}

/** Recent hourly rewards — read-only; filters live in the URL. */
function RewardsCard() {
  const { get, set } = useUrlState();
  const page = intParam(get('rpage'), 1);
  const dateKey = dayParam(get('rday'));

  const { data, isLoading } = useQuery({
    queryKey: [...REWARDS_KEY, page, dateKey],
    queryFn: async () =>
      (
        await api.get<{ items: BonusRewardRow[]; total: number }>('/admin/time-bonus/rewards', {
          params: { page, pageSize: 25, ...(dateKey ? { dateKey } : {}) },
        })
      ).data,
  });

  const columns: TableColumnsType<BonusRewardRow> = [
    {
      title: 'Listener',
      render: (_: unknown, r) => <ListenerName listener={r.listener} listenerId={r.listener.id} />,
    },
    {
      title: 'Reward',
      render: (_: unknown, r) => (
        <span>
          {r.periodName} — Hour {r.seq} · {paiseToRupees(r.rewardPaise)}
        </span>
      ),
    },
    { title: 'Window date', dataIndex: 'dateKey', width: 120 },
    {
      title: 'Status',
      width: 110,
      render: (_: unknown, r) => (
        <Tag color={r.status === 'credited' ? 'green' : 'orange'}>{r.status}</Tag>
      ),
    },
    {
      title: 'Credited',
      width: 180,
      render: (_: unknown, r) => (r.creditedAt ? new Date(r.creditedAt).toLocaleString() : '—'),
    },
  ];

  return (
    <Card
      title="Recent hourly rewards"
      extra={
        <Input
          allowClear
          placeholder="Filter by date (YYYY-MM-DD)"
          style={{ width: 220 }}
          defaultValue={dateKey ?? ''}
          onChange={(e) => set({ rday: e.target.value.trim() || null, rpage: null })}
        />
      }
    >
      <Table<BonusRewardRow>
        rowKey="id"
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={data?.items ?? []}
        pagination={{
          current: page,
          pageSize: 25,
          total: data?.total ?? 0,
          showSizeChanger: false,
          onChange: (p) => set({ rpage: p === 1 ? null : String(p) }),
        }}
        locale={{ emptyText: 'No bonus rewards yet' }}
      />
    </Card>
  );
}
